import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const TemperatureChartDialog = ({ open, onClose, vitals = [] }) => {
  const labels = vitals.map((v) =>
    v.time ? `${v.date} ${v.time}` : v.date
  );

  // Filter out null/undefined values and convert to numbers
  const temps = vitals.map((v) => {
    const temp = v.temperature ? parseFloat(v.temperature) : null;
    return temp && !isNaN(temp) ? temp : null;
  });

  // Count valid temperature readings
  const validTemps = temps.filter(t => t !== null);
  const hasInsufficientData = validTemps.length < 2;

  const data = {
    labels,
    datasets: [
      {
        label: "Body Temperature (°C)",
        data: temps,
        borderColor: "#e53935",
        backgroundColor: "rgba(229, 57, 53, 0.25)",
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: "#e53935",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        tension: 0.3,
        spanGaps: true,
        borderWidth: 3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
        labels: { 
          font: { size: 14, weight: "bold" },
          padding: 15,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Temperature: ${context.parsed.y}°C`;
          }
        },
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: 12,
        titleFont: { size: 14 },
        bodyFont: { size: 13 },
      }
    },
    scales: {
      y: {
        suggestedMin: 35,
        suggestedMax: 40,
        title: {
          display: true,
          text: "Temperature (°C)",
          font: { size: 14, weight: 'bold' },
        },
        ticks: {
          callback: function(value) {
            return value + '°C';
          }
        },
        grid: {
          color: 'rgba(0,0,0,0.05)',
        }
      },
      x: {
        title: {
          display: true,
          text: "Date & Time",
          font: { size: 14, weight: 'bold' },
        },
        grid: {
          color: 'rgba(0,0,0,0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        }
      },
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
         background: "linear-gradient(135deg, #19b934ff 0%, #19b934ff 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Temperature Chart
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: 'background.default' }}>
        {vitals.length === 0 && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              No temperature data available
            </Typography>
            <Typography variant="caption">
              Temperature readings will appear here once data is provided.
            </Typography>
          </Alert>
        )}

        {vitals.length > 0 && hasInsufficientData && (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Insufficient data for trend visualization
            </Typography>
            <Typography variant="caption">
              At least 2 temperature readings are required to display a trend line. Currently showing {validTemps.length} reading(s).
            </Typography>
          </Alert>
        )}

        {vitals.length > 0 && (
          <>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Temperature Trend {validTemps.length > 0 && `(${validTemps.length} reading${validTemps.length !== 1 ? 's' : ''})`}
              </Typography>

              <Box sx={{ minHeight: 300 }}>
                <Line data={data} options={options} />
              </Box>
            </Paper>

            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "grey.100" }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                Observations
              </Typography>

              {vitals.map((v, index) => (
                <Typography key={index} sx={{ fontSize: "0.9rem", mb: 0.5 }}>
                  • <b>{v.temperature}°C</b> on <b>{v.date}</b>{" "}
                  {v.time && <>at <b>{v.time}</b></>}
                </Typography>
              ))}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemperatureChartDialog;